import { inject, Injectable } from '@angular/core';
import { Observable, of, map, catchError, shareReplay } from 'rxjs';
import { ApiService } from '../api-services/base-api/api.service';
import { FeatureToggle } from '../models/feature-toggle';

@Injectable({
  providedIn: 'root',
})
export class FeatureToggleService {
  private readonly api = inject(ApiService);
  private togglesCache$: Observable<FeatureToggle[]> | null = null;

  /**
   * Fetch all feature toggles from the backend.
   * Results are cached and shared across subscribers.
   */
  getToggles(): Observable<FeatureToggle[]> {
    if (!this.togglesCache$) {
      this.togglesCache$ = this.api.get<FeatureToggle[]>('feature-toggles').pipe(
        catchError(() => of([])),  // graceful fallback: all toggles off
        shareReplay(1),
      );
    }
    return this.togglesCache$;
  }

  /**
   * Check whether a specific feature is enabled.
   * Returns false if the toggle doesn't exist or the call fails.
   */
  isEnabled(name: string): Observable<boolean> {
    return this.getToggles().pipe(
      map(toggles => {
        const toggle = toggles.find(t => t.name === name);
        return toggle ? toggle.isEnabled : false;
      }),
    );
  }

  /** Clear the internal cache so the next read fetches fresh data. */
  clearCache(): void {
    this.togglesCache$ = null;
  }
}
